import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Row } from "../types";
import { SUBABAS_SERINGAS } from "../config";
import {
  api,
  ApiError,
  type ClienteSeringa,
  type DetalheSaidaSeringas,
  type FaixaSeringas,
  type ResultadoLoteSeringas,
  type ResumoSeringas,
} from "../api";
import { formatDate } from "../format";
import { compararNomes } from "../ordem";
import { usePodeExcluir } from "../permissoes";
import CameraScanner from "./CameraScanner";
import { chaveDoCodigo } from "../leitor-camera";
// O desenho do código de barras (Code 128) e a folha de etiquetas: sem eles, a
// seringa numerada pelo sistema nunca poderia ser bipada. Ver src/codigo-barras.ts.
import { code128SVG, htmlEtiquetas } from "../codigo-barras";
import {
  ICONS,
  IconSearch,
  IconPlus,
  IconTrash,
  IconCheck,
  IconCamera,
  IconBox,
  IconTruck,
  IconAlert,
  IconClose,
  IconSend,
  IconSyringe,
  IconDownload,
  IconFlask,
} from "../icons";

/**
 * CONTROLE DE SERINGAS — as quatro subabas, em uma tela só.
 *
 * O desenho é o mesmo da aba "Análises" (ver AnalisesView.tsx): o SUBMENU é o
 * do menu lateral, e esta tela só obedece ao `subKey` que o App manda. Os
 * mesmos quatro aparecem como cartões quando nenhum está escolhido, para quem
 * abre a aba pela primeira vez ler o fluxo inteiro sem ninguém explicar.
 *
 *   Cadastro   bipar a caixa de seringas novas — elas nascem "Em estoque"
 *   Estoque    onde está cada uma: aqui, em serviço ou com o cliente
 *   Saída      abre o lote (OS ou cliente), bipa o que vai e confirma
 *   Retorno    abre o lote que saiu, bipa o que voltou — volta "Em estoque"
 *
 * TODA TELA DE BIPAGEM SEGUE A REGRA DA BANCADA (a mesma da conferência de
 * material): o campo do leitor NUNCA perde o foco, duplicado é aviso amarelo e
 * não erro, e o contador fica grande e permanente — é ele que decide se a
 * caixa pode ser fechada.
 */
interface Props {
  /** Qual subaba o menu lateral escolheu (null = mostra o painel de cartões). */
  subKey: string | null;
  onSubKeyChange: (key: string | null) => void;
  /** Avisa o App para recarregar os contadores do menu. */
  onDataChanged: () => void;
}

type Aviso = { tipo: "ok" | "atencao" | "erro"; texto: string } | null;

/** As situações possíveis de uma seringa (o filtro do estoque usa esta lista). */
const SITUACOES = [
  "Em estoque",
  "Em serviço",
  "Com cliente",
  "Em laboratório externo",
  "Faltante",
  "Baixada",
] as const;

/** Destino "Laboratório externo": análise terceirizada — volta bipando, como os outros. */
const LAB_EXTERNO = "Laboratório externo";

/** Para onde a saída foi, em poucas palavras (lista, cabeçalho, estoque). */
function destinoCurto(x: Row): string {
  if (x.destino === LAB_EXTERNO) return String(x.laboratorio_externo || "laboratório não informado");
  if (x.destino === "Cliente") return String(x.cliente_nome || "cliente não informado");
  return `OS ${x.os_numero || "não informada"}`;
}

/** A cor da pílula de cada situação — sóbria: vermelho só para a baixa. */
function tomDaSituacao(situacao: string): string {
  if (situacao === "Em estoque") return "pill-ok";
  if (situacao === "Baixada") return "pill-danger";
  if (situacao === "Faltante") return "pill-warn";
  return "pill-neutral";
}

/** Onde a seringa está, em uma linha ("OS 2661" / "Fulano Ltda"). */
function ondeEsta(row: Row): string {
  if (!row.saida_id) return "—";
  if (row.destino === LAB_EXTERNO) {
    return `Lab. externo: ${row.laboratorio_externo || "não informado"}` +
      (row.os_numero ? ` · OS ${row.os_numero}` : "");
  }
  return destinoCurto(row);
}

/** Data de hoje no formato do <input type="date"> (sem passar pelo fuso UTC). */
function hojeISO(): string {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
}

export default function SeringasView({ subKey, onSubKeyChange, onDataChanged }: Props) {
  const [resumo, setResumo] = useState<ResumoSeringas | null>(null);
  /** A saída aberta na tela (vale para a subaba de saída e a de retorno). */
  const [saidaAberta, setSaidaAberta] = useState<number | null>(null);

  const carregarResumo = useCallback(async () => {
    try {
      setResumo(await api.seringas.resumo());
    } catch {
      // Falha de rede não pode esconder a aba: sem os números, o menu e o
      // painel aparecem mesmo assim.
      setResumo(null);
    }
  }, []);

  useEffect(() => {
    void carregarResumo();
  }, [carregarResumo]);

  /** Recarrega os números daqui e avisa o App (que atualiza o menu). */
  const mudou = useCallback(() => {
    void carregarResumo();
    onDataChanged();
  }, [carregarResumo, onDataChanged]);

  // Trocar de subaba fecha a saída que estava aberta: manter a de antes faria
  // a pessoa cair no Retorno já dentro de um lote que ela nem escolheu.
  useEffect(() => {
    setSaidaAberta(null);
  }, [subKey]);

  const sub = SUBABAS_SERINGAS.find((s) => s.key === subKey);

  return (
    <div className="crud">
      {sub && (
        <div className="an-trilha">
          <button
            className="btn-secondary btn-sm"
            onClick={() => (saidaAberta ? setSaidaAberta(null) : onSubKeyChange(null))}
          >
            ← Voltar
          </button>
          <span
            className="an-trilha-item"
            onClick={() => onSubKeyChange(null)}
            role="button"
            tabIndex={0}
          >
            Controle de Seringas
          </span>
          <span className="an-trilha-sep">›</span>
          {saidaAberta ? (
            <>
              <span
                className="an-trilha-item"
                onClick={() => setSaidaAberta(null)}
                role="button"
                tabIndex={0}
              >
                {sub.titulo}
              </span>
              <span className="an-trilha-sep">›</span>
              <strong>Saída #{saidaAberta}</strong>
            </>
          ) : (
            <strong className="an-trilha-atual">{sub.titulo}</strong>
          )}
        </div>
      )}

      {!sub && <PainelSeringas resumo={resumo} onEscolher={onSubKeyChange} />}

      {subKey === "cadastro" && <CadastroSeringas onMudou={mudou} resumo={resumo} />}
      {subKey === "estoque" && <EstoqueSeringas onMudou={mudou} resumo={resumo} />}
      {subKey === "saida" && (
        <SaidaSeringas
          saidaAberta={saidaAberta}
          onAbrirSaida={setSaidaAberta}
          onMudou={mudou}
        />
      )}
      {subKey === "retorno" && (
        <RetornoSeringas
          saidaAberta={saidaAberta}
          onAbrirSaida={setSaidaAberta}
          onMudou={mudou}
        />
      )}
    </div>
  );
}

/* ======================================================================
 *  PAINEL — os quatro cartões, com o número que importa em cada um
 * ==================================================================== */

function PainelSeringas({
  resumo,
  onEscolher,
}: {
  resumo: ResumoSeringas | null;
  onEscolher: (k: string) => void;
}) {
  /*
   * Cada cartão mostra O NÚMERO DAQUELA ETAPA, não o total geral: uma fileira
   * com o mesmo número quatro vezes não ajuda ninguém a decidir onde clicar.
   */
  const numeros: Record<string, { valor: number; rotulo: string }> = {
    cadastro: { valor: resumo?.estoque.total ?? 0, rotulo: "seringa(s) cadastrada(s)" },
    estoque: { valor: resumo?.estoque.emEstoque ?? 0, rotulo: "disponível(is) no laboratório" },
    saida: { valor: resumo?.saidas.emSeparacao ?? 0, rotulo: "saída(s) em separação" },
    retorno: { valor: resumo?.aguardandoRetorno ?? 0, rotulo: "seringa(s) para voltar" },
  };

  return (
    <>
      <div className="dash-topo">
        <h3 className="dash-secao">O caminho da seringa</h3>
        <span className="muted dash-dica">
          Escolha a etapa aqui ou pelo menu, que desce dentro de “Controle de Seringas”
        </span>
      </div>

      <div className="cards an-tipos">
        {SUBABAS_SERINGAS.map((s) => {
          const Icon = ICONS[s.icone];
          const n = numeros[s.key]!;
          return (
            <button
              key={s.key}
              className={`card card-clicavel an-tipo tone-${s.tom}`}
              onClick={() => onEscolher(s.key)}
            >
              <span className="card-icon">
                <Icon size={22} />
              </span>
              <span className="card-body an-tipo-corpo">
                <span className="an-tipo-nome">
                  {s.titulo}
                  <em className={`an-selo tom-${s.tom}`}>{s.sigla}</em>
                </span>
                <span className="an-tipo-numeros">
                  <strong>{n.valor}</strong> {n.rotulo}
                </span>
                <span className="an-tipo-desc">{s.descricao}</span>
              </span>
              <span className="an-tipo-seta" aria-hidden="true">
                ›
              </span>
            </button>
          );
        })}
      </div>

      {resumo && (
        <>
          <div className="dash-topo">
            <h3 className="dash-secao">Onde estão as seringas agora</h3>
            <span className="muted dash-dica">
              {resumo.estoque.total} no cadastro — a soma das situações
            </span>
          </div>
          <div className="ser-resumo">
            <span className="ser-resumo-item">
              <strong>{resumo.estoque.emEstoque}</strong> em estoque
            </span>
            <span className="ser-resumo-item">
              <strong>{resumo.estoque.emServico}</strong> em serviço
            </span>
            <span className="ser-resumo-item">
              <strong>{resumo.estoque.comCliente}</strong> com o cliente
            </span>
            <span className="ser-resumo-item">
              <strong>{resumo.estoque.emLabExterno ?? 0}</strong> em laboratório externo
            </span>
            <span className="ser-resumo-item">
              <strong>{resumo.estoque.faltantes ?? 0}</strong> faltante(s)
            </span>
            <span className="ser-resumo-item">
              <strong>{resumo.estoque.baixadas}</strong> baixada(s)
            </span>
          </div>
        </>
      )}
    </>
  );
}

/* ======================================================================
 *  O CAMPO DO LEITOR — o mesmo em cadastro, saída e retorno
 * ==================================================================== */

/**
 * A barra de bipagem. Existe uma só, e não uma por tela, porque as três
 * bipagens têm exatamente as mesmas manias de bancada: refocar sozinha depois
 * de cada leitura, aceitar digitação para quando o leitor quebra e abrir a
 * câmera do celular pelo mesmo botão.
 */
function BarraLeitor({
  campoRef,
  codigo,
  setCodigo,
  onBipar,
  ocupado,
  camAberta,
  onAbrirCamera,
  rotulo,
}: {
  campoRef: React.RefObject<HTMLInputElement>;
  codigo: string;
  setCodigo: (v: string) => void;
  onBipar: (e: React.FormEvent) => void;
  ocupado: boolean;
  camAberta: boolean;
  onAbrirCamera: () => void;
  rotulo: string;
}) {
  return (
    <form className="conf-leitor" onSubmit={onBipar}>
      <label>
        <span>{rotulo}</span>
        <input
          ref={campoRef}
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          placeholder="aguardando o leitor…"
          autoFocus
          autoComplete="off"
          spellCheck={false}
          disabled={ocupado}
        />
      </label>
      <button type="submit" className="btn-primary" disabled={ocupado || !codigo.trim()}>
        <IconCheck size={16} /> Bipar
      </button>
      {/*
        type="button" é obrigatório: dentro de um <form> o padrão de um botão é
        ENVIAR — sem isso, abrir a câmera dispararia uma bipagem em branco.
      */}
      {!camAberta && (
        <button type="button" className="btn-secondary" onClick={onAbrirCamera}>
          <IconCamera size={16} /> Ler pela câmera
        </button>
      )}
    </form>
  );
}

/** O contador grande (bipadas × previstas) — é o número que fecha a caixa. */
function Contador({
  feito,
  previsto,
  rotuloFeito,
  rotuloPrevisto,
}: {
  feito: number;
  previsto: number;
  rotuloFeito: string;
  rotuloPrevisto: string;
}) {
  const diferenca = feito - previsto;
  const tom = previsto === 0 ? "" : diferenca === 0 ? " ok" : " alerta";
  return (
    <div className={`conf-contador${tom}`}>
      <div>
        <span className="conf-num">{feito}</span>
        <span className="conf-rot">{rotuloFeito}</span>
      </div>
      <div className="conf-sep">de</div>
      <div>
        <span className="conf-num">{previsto || "—"}</span>
        <span className="conf-rot">{rotuloPrevisto}</span>
      </div>
      {previsto > 0 && (
        <div className="conf-dif">
          {diferenca === 0
            ? "a conta fechou"
            : diferenca > 0
              ? `${diferenca} a mais`
              : `${Math.abs(diferenca)} faltando`}
        </div>
      )}
    </div>
  );
}

/* ======================================================================
 *  SUBABA 1 — CADASTRO
 * ==================================================================== */

/**
 * A ENTRADA DE ESTOQUE EM ANDAMENTO, guardada no navegador.
 *
 * Bipar 50 seringas leva minutos, e nesse tempo acontece de tudo: a tela é
 * fechada sem querer, o celular bloqueia, o navegador recarrega. As seringas
 * já bipadas estão salvas no banco (cada leitura é um cadastro), mas a META
 * ("estou cadastrando uma caixa de 50") só existe aqui — e sem ela a pessoa
 * volta sem saber em quantas parou. Por isso mora no localStorage, e não só
 * no estado do React.
 */
const CHAVE_ENTRADA = "sercamp-lab-entrada-seringas";

interface EntradaEmAndamento {
  tipo: string;
  /**
   * Quantas seringas esta caixa tem — é o TETO da bipagem: com 10 lançadas, a
   * 11ª leitura é recusada. (0 só existe em entrada antiga, de antes do teto.)
   */
  meta: number;
  /** Os códigos já bipados nesta entrada, o mais novo primeiro. */
  codigos: string[];
  observacao?: string;
}

function lerEntrada(): EntradaEmAndamento | null {
  try {
    const bruto = localStorage.getItem(CHAVE_ENTRADA);
    if (!bruto) return null;
    const e = JSON.parse(bruto);
    if (!e || typeof e !== "object" || !Array.isArray(e.codigos)) return null;
    return e as EntradaEmAndamento;
  } catch {
    return null;
  }
}

function gravarEntrada(e: EntradaEmAndamento | null) {
  try {
    if (e) localStorage.setItem(CHAVE_ENTRADA, JSON.stringify(e));
    else localStorage.removeItem(CHAVE_ENTRADA);
  } catch {
    /* navegador sem localStorage: a entrada só não sobrevive ao recarregar */
  }
}

/**
 * CADASTRO — a entrada de estoque, do começo ao fim.
 *
 * A primeira versão desta tela era um campo de leitor solto, sem começo nem
 * fim: não havia botão de "cadastrar", não se dizia quantas seringas a caixa
 * tinha e nada contava quanto faltava. Quem bipava não sabia se tinha acabado.
 *
 * Agora é uma ENTRADA, com as mesmas três partes da saída:
 *   1. o botão "Cadastrar seringas" abre o formulário (tipo e QUANTIDADE);
 *   2. a caixa entra de um dos dois jeitos, escolhido ali mesmo —
 *      BIPANDO uma a uma (a que já vem com etiqueta do fabricante) ou
 *      SÓ PELA QUANTIDADE, com o sistema numerando (a que vem sem etiqueta);
 *   3. o contador grande mostra o que falta, e "Finalizar" fecha a entrada.
 */
function CadastroSeringas({
  onMudou,
  resumo,
}: {
  onMudou: () => void;
  resumo: ResumoSeringas | null;
}) {
  const [entrada, setEntrada] = useState<EntradaEmAndamento | null>(lerEntrada);
  const [formAberto, setFormAberto] = useState(false);
  /** O resultado de uma entrada por quantidade (é dele que saem as etiquetas). */
  const [lote, setLote] = useState<ResultadoLoteSeringas | null>(null);

  const [codigo, setCodigo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<Aviso>(null);
  const [camAberta, setCamAberta] = useState(false);

  const campoRef = useRef<HTMLInputElement>(null);
  /** Os códigos já lidos nesta entrada (o `registrar` precisa deles para o teto). */
  const jaLidosRef = useRef<Set<string>>(new Set());
  const focar = useCallback(() => {
    if (!camAberta) setTimeout(() => campoRef.current?.focus(), 0);
  }, [camAberta]);

  /** Guarda no navegador toda vez que a entrada muda (ver CHAVE_ENTRADA). */
  const atualizarEntrada = useCallback((e: EntradaEmAndamento | null) => {
    setEntrada(e);
    gravarEntrada(e);
  }, []);

  const registrar = useCallback(
    async (bruto: string, origem: "leitor" | "camera"): Promise<"novo" | "duplicado" | "erro"> => {
      const valor = bruto.trim();
      if (!valor || !entrada) return "erro";
      // O TETO: a caixa lançada com 10 aceita 10 leituras. A que passa disso
      // nem chega ao servidor — senão entraria no estoque uma seringa que a
      // pessoa não lançou.
      if (entrada.meta > 0 && entrada.codigos.length >= entrada.meta) {
        if (jaLidosRef.current.has(chaveDoCodigo(valor))) {
          setAviso({ tipo: "atencao", texto: `"${valor}" já está nesta entrada.` });
          return "duplicado";
        }
        setAviso({
          tipo: "erro",
          texto:
            `A entrada já tem as ${entrada.meta} seringa(s) lançadas — "${valor}" não entrou. ` +
            "Para trocar uma, tire outra da lista antes.",
        });
        return "erro";
      }
      try {
        const r = await api.seringas.cadastrar(valor, {
          tipo: entrada.tipo || undefined,
          observacao: entrada.observacao || undefined,
          origem,
        });
        if (r.duplicada) {
          setAviso({ tipo: "atencao", texto: r.mensagem ?? `"${valor}" já está cadastrada.` });
          return "duplicado";
        }
        const codigoNovo = String(r.seringa?.codigo ?? valor);
        const atualizada = { ...entrada, codigos: [codigoNovo, ...entrada.codigos] };
        atualizarEntrada(atualizada);
        const falta = atualizada.meta - atualizada.codigos.length;
        // Caixa fechada: a câmera para de ler sozinha, para não seguir
        // recusando seringa atrás de seringa.
        if (atualizada.meta > 0 && falta <= 0) setCamAberta(false);
        setAviso({
          tipo: "ok",
          texto:
            atualizada.meta > 0 && falta === 0
              ? `${codigoNovo} entrou — a caixa fechou (${atualizada.meta} de ${atualizada.meta}).`
              : atualizada.meta > 0
                ? `${codigoNovo} entrou no estoque. Faltam ${falta}.`
                : `${codigoNovo} entrou no estoque.`,
        });
        onMudou();
        return "novo";
      } catch (err) {
        setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao cadastrar." });
        return "erro";
      }
    },
    [entrada, atualizarEntrada, onMudou],
  );

  async function bipar(e: React.FormEvent) {
    e.preventDefault();
    const valor = codigo.trim();
    if (!valor || ocupado) return;
    setOcupado(true);
    try {
      await registrar(valor, "leitor");
    } finally {
      // Limpa até depois de erro: o leitor digita por cima do que está no campo
      // e a leitura seguinte grudaria na errada. O código lido fica no aviso.
      setCodigo("");
      setOcupado(false);
      focar();
    }
  }

  const jaLidos = useMemo(
    () => new Set((entrada?.codigos ?? []).map((c) => chaveDoCodigo(c))),
    [entrada],
  );
  jaLidosRef.current = jaLidos;

  /**
   * TIRAR DA LISTA — a seringa bipada por engano, ou que quebrou na mão antes
   * de ir para a prateleira. Apaga o cadastro dela (o servidor só aceita se
   * ela nunca circulou) e devolve a vaga ao contador.
   */
  async function tirarDaLista(codigoLido: string) {
    if (!entrada) return;
    if (!confirm(`Tirar a seringa ${codigoLido} desta entrada?\n\nEla sai do estoque.`)) return;
    try {
      await api.seringas.desfazerCadastro(codigoLido);
      const chave = chaveDoCodigo(codigoLido);
      atualizarEntrada({
        ...entrada,
        codigos: entrada.codigos.filter((c) => chaveDoCodigo(c) !== chave),
      });
      setAviso({ tipo: "atencao", texto: `${codigoLido} foi tirada da entrada e do estoque.` });
      onMudou();
    } catch (err) {
      setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao tirar a seringa." });
    } finally {
      focar();
    }
  }

  function finalizar() {
    const quantas = entrada?.codigos.length ?? 0;
    atualizarEntrada(null);
    setAviso(
      quantas > 0
        ? { tipo: "ok", texto: `Entrada encerrada — ${quantas} seringa(s) foram para o estoque.` }
        : null,
    );
    onMudou();
  }

  /* --------------------------------------------------------------
   *  TELA A — o resultado de uma entrada por quantidade (etiquetas)
   * ------------------------------------------------------------ */
  if (lote) {
    return (
      <EtiquetasDoLote
        lote={lote}
        onNovaEntrada={() => {
          setLote(null);
          setFormAberto(true);
        }}
        onSair={() => setLote(null)}
      />
    );
  }

  /* --------------------------------------------------------------
   *  TELA B — nenhuma entrada aberta: o estoque e o botão
   * ------------------------------------------------------------ */
  if (!entrada) {
    return (
      <>
        {formAberto && (
          <NovaEntrada
            onCancelar={() => setFormAberto(false)}
            onBipar={(e) => {
              setFormAberto(false);
              atualizarEntrada(e);
              setAviso(null);
              setTimeout(() => campoRef.current?.focus(), 50);
            }}
            onLote={(r) => {
              setFormAberto(false);
              setLote(r);
              onMudou();
            }}
          />
        )}

        <div className="ser-inicio">
          <div className="ser-inicio-txt">
            <h3>Entrada de seringas no estoque</h3>
            <p className="muted">
              Chegou caixa nova? Diga o tipo e <strong>quantas são</strong>, e escolha como elas
              entram: bipando uma a uma (quando já vêm com etiqueta) ou só pela quantidade — aí o
              sistema numera e você imprime as etiquetas. Na bipagem, entram só as seringas
              lançadas.
            </p>
            <button className="btn-primary btn-grande" onClick={() => setFormAberto(true)}>
              <IconPlus size={18} /> Cadastrar seringas
            </button>
          </div>

          <div className="ser-inicio-num">
            <span className="ser-inicio-valor">{resumo?.estoque.emEstoque ?? 0}</span>
            <span className="ser-inicio-rot">em estoque agora</span>
            <span className="ser-inicio-pe">
              {resumo?.estoque.total ?? 0} seringa(s) no cadastro ·{" "}
              {(resumo?.estoque.emServico ?? 0) +
                (resumo?.estoque.comCliente ?? 0) +
                (resumo?.estoque.emLabExterno ?? 0)}{" "}
              fora
            </span>
          </div>
        </div>

        {aviso && <div className={`conf-aviso ${aviso.tipo}`}>{aviso.texto}</div>}
      </>
    );
  }

  /* --------------------------------------------------------------
   *  TELA C — entrada em andamento: a bipagem, com meta e contador
   * ------------------------------------------------------------ */
  const feitas = entrada.codigos.length;
  const completa = entrada.meta > 0 && feitas >= entrada.meta;

  return (
    <div className="ser-bipagem" onClick={focar}>
      <div className="ser-cabecalho">
        <div className="ser-cab-txt">
          <h3>Cadastrando {entrada.meta > 0 ? `${entrada.meta} seringa(s)` : "seringas"}</h3>
          <p className="muted">
            {entrada.tipo || "sem tipo informado"} — bipe uma a uma; cada leitura entra no estoque
            na hora{entrada.meta > 0 ? `, até completar as ${entrada.meta}` : ""}.
          </p>
        </div>
        <span className="badge">{resumo?.estoque.emEstoque ?? 0} em estoque</span>
      </div>

      <Contador
        feito={feitas}
        previsto={entrada.meta}
        rotuloFeito="cadastradas"
        rotuloPrevisto="na caixa"
      />

      {/* Caixa completa: o leitor some — não há mais vaga para bipar. */}
      {!completa && (
        <BarraLeitor
          campoRef={campoRef}
          codigo={codigo}
          setCodigo={setCodigo}
          onBipar={bipar}
          ocupado={ocupado}
          camAberta={camAberta}
          onAbrirCamera={() => setCamAberta(true)}
          rotulo="Bipe o código de barras da seringa (ou digite e pressione Enter)"
        />
      )}

      {camAberta && !completa && (
        <CameraScanner
          jaConferidos={jaLidos}
          onCodigo={(texto) => registrar(texto, "camera")}
          onFechar={() => {
            setCamAberta(false);
            setTimeout(() => campoRef.current?.focus(), 0);
          }}
          onDigitar={() => {
            setCamAberta(false);
            setTimeout(() => campoRef.current?.focus(), 0);
          }}
        />
      )}

      {aviso && !(completa && aviso.tipo === "ok") && (
        <div className={`conf-aviso ${aviso.tipo}`}>{aviso.texto}</div>
      )}
      {completa && (
        <div className="conf-aviso ok">
          A caixa fechou: {feitas} de {entrada.meta}. Clique em “Finalizar entrada” — ou tire
          uma seringa da lista para bipar outra no lugar.
        </div>
      )}

      <div className="conf-lista">
        {feitas === 0 ? (
          <div className="empty">Nada cadastrado ainda — bipe a primeira seringa.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Código</th>
                <th>Tipo</th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {entrada.codigos.map((c, n) => (
                <tr key={c}>
                  <td>{feitas - n}</td>
                  <td>
                    <strong>{c}</strong>
                  </td>
                  <td>{entrada.tipo || "—"}</td>
                  <td className="col-actions">
                    <button
                      className="icon-btn icon-btn-danger"
                      title="Tirar esta seringa da entrada"
                      onClick={(e) => {
                        e.stopPropagation();
                        void tirarDaLista(c);
                      }}
                    >
                      <IconTrash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="ser-rodape">
        <button className="btn-primary" onClick={finalizar}>
          <IconCheck size={16} /> Finalizar entrada
        </button>
        <span className="muted">
          O que já foi bipado está salvo no estoque — finalizar só encerra a contagem desta caixa.
        </span>
      </div>
    </div>
  );
}

/**
 * O FORMULÁRIO DA ENTRADA — é aqui que se diz QUANTAS são e COMO elas entram.
 *
 * Os dois caminhos existem porque as duas caixas existem: a que vem com o
 * código do fabricante impresso (bipa) e a que vem lisa (o sistema numera e a
 * pessoa imprime a etiqueta). Escolher aqui, e não em duas telas diferentes,
 * é o que mantém uma coisa só chamada "cadastrar seringas".
 */
function NovaEntrada({
  onCancelar,
  onBipar,
  onLote,
}: {
  onCancelar: () => void;
  onBipar: (entrada: EntradaEmAndamento) => void;
  onLote: (r: ResultadoLoteSeringas) => void;
}) {
  const [tipo, setTipo] = useState("");
  const [quantidade, setQuantidade] = useState("");
  const [observacao, setObservacao] = useState("");
  const [modo, setModo] = useState<"bipar" | "auto">("bipar");
  const [prefixo, setPrefixo] = useState("SER-");
  const [faixa, setFaixa] = useState<FaixaSeringas | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const qtd = Math.max(0, Math.trunc(Number(quantidade) || 0));

  /*
   * A PRÉVIA DA NUMERAÇÃO vem do servidor, e não de uma conta feita aqui: é
   * ele que sabe onde o prefixo parou. Mostrar "SER-0001 a SER-0050" quando na
   * verdade a base já vai na 0120 seria pior do que não mostrar nada.
   */
  useEffect(() => {
    if (modo !== "auto" || qtd < 1) {
      setFaixa(null);
      return;
    }
    const controller = new AbortController();
    const t = setTimeout(() => {
      api.seringas
        .proximaFaixa(prefixo, qtd, controller.signal)
        .then(setFaixa)
        .catch(() => setFaixa(null));
    }, 350);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [modo, qtd, prefixo]);

  async function salvar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);

    // A quantidade é obrigatória nos dois jeitos: na bipagem ela é o teto
    // (lançou 10, entram 10), e sem ela nada impediria de bipar a mais.
    if (qtd < 1) {
      setErro("Informe quantas seringas estão entrando.");
      return;
    }

    if (modo === "bipar") {
      onBipar({ tipo: tipo.trim(), meta: qtd, codigos: [], observacao: observacao.trim() });
      return;
    }

    setSalvando(true);
    try {
      const r = await api.seringas.cadastrarLote({
        quantidade: qtd,
        tipo: tipo.trim(),
        prefixo,
        observacao: observacao.trim(),
      });
      onLote(r);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Erro ao cadastrar a caixa.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancelar}>
      <form className="modal-card modal-wide" onMouseDown={(e) => e.stopPropagation()} onSubmit={salvar}>
        <div className="modal-head rf-head">
          <div className="rf-head-txt">
            <span className="rf-head-icon">
              <IconSyringe size={20} />
            </span>
            <div>
              <h3>Cadastrar seringas</h3>
              <p className="rf-head-sub">Uma caixa nova entrando no estoque do laboratório.</p>
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onCancelar} title="Fechar">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body ser-form">
          <div className="ser-form-linha">
            <label className="ser-campo">
              <span>Tipo / modelo</span>
              <input
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                placeholder="ex.: seringa de vidro 50 mL"
                autoFocus
              />
            </label>
            <label className="ser-campo ser-campo-qtd">
              <span>Quantidade</span>
              <input
                type="number"
                min={1}
                step={1}
                value={quantidade}
                onChange={(e) => setQuantidade(e.target.value)}
                placeholder="ex.: 50"
              />
            </label>
          </div>

          <div className="ser-escolha">
            <span className="ser-escolha-rot">Como elas entram</span>
            <div className="ser-escolha-botoes">
              <button
                type="button"
                className={`ser-chip ${modo === "bipar" ? "ativo" : ""}`}
                onClick={() => setModo("bipar")}
              >
                <IconCamera size={16} /> Vou bipar uma a uma
              </button>
              <button
                type="button"
                className={`ser-chip ${modo === "auto" ? "ativo" : ""}`}
                onClick={() => setModo("auto")}
              >
                <IconPlus size={16} /> Só a quantidade (o sistema numera)
              </button>
            </div>
          </div>

          {modo === "bipar" ? (
            <p className="muted">
              Para a caixa que já vem com código de barras. A área de leitura abre em seguida, com
              o contador {qtd > 0 ? `“0 de ${qtd}”` : "da quantidade informada"} — e só aceita
              essa quantidade de seringas.
            </p>
          ) : (
            <>
              <div className="ser-form-linha">
                <label className="ser-campo">
                  <span>Começo do código</span>
                  <input
                    value={prefixo}
                    /*
                     * A MESMA limpeza que o servidor faz (letras, números e
                     * - _ .), aplicada enquanto se digita. Sem isto, quem
                     * escrevia "lab/2026 " via o campo de um jeito e a
                     * numeração sair de outro ("LAB20260001") — a tela mentia
                     * sobre o que ia ser gravado.
                     */
                    onChange={(e) =>
                      setPrefixo(e.target.value.toUpperCase().replace(/[^A-Z0-9\-_.]/g, ""))
                    }
                    placeholder="SER-"
                  />
                  <em className="ser-dica">
                    letras, números e - _ . — termine com “-” para o número não colar
                  </em>
                </label>
                <div className="ser-campo">
                  <span>Numeração que vai ser criada</span>
                  <div className="ser-faixa">
                    {qtd < 1 ? (
                      "informe a quantidade"
                    ) : faixa && faixa.primeiro ? (
                      <>
                        <strong>{faixa.primeiro}</strong> até <strong>{faixa.ultimo}</strong>
                      </>
                    ) : (
                      "calculando…"
                    )}
                  </div>
                </div>
              </div>
              <p className="muted">
                Para a caixa que chega sem etiqueta. As {qtd || "N"} seringas entram no estoque de
                uma vez e a tela seguinte imprime as etiquetas com o código de barras, prontas para
                colar — é ele que vai ser bipado na saída e no retorno.
              </p>
            </>
          )}

          <label className="ser-campo">
            <span>Observação</span>
            <input
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="opcional — nota de compra, fornecedor…"
            />
          </label>

          {erro && <div className="conf-aviso erro">{erro}</div>}
        </div>

        <div className="modal-foot">
          <button type="submit" className="btn-primary" disabled={salvando}>
            <IconCheck size={16} />{" "}
            {modo === "bipar" ? "Abrir a bipagem" : `Cadastrar ${qtd || ""} seringa(s)`}
          </button>
          <button type="button" className="btn-secondary" onClick={onCancelar}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * DEPOIS DA ENTRADA POR QUANTIDADE — as etiquetas.
 *
 * Esta tela não é um "pronto, deu certo": ela é a metade que falta do
 * cadastro. Uma seringa numerada só pelo sistema não pode ser bipada enquanto
 * ninguém colar o código nela — e é por isso que o botão de imprimir fica
 * grande, aqui, e não escondido no estoque.
 */
function EtiquetasDoLote({
  lote,
  onNovaEntrada,
  onSair,
}: {
  lote: ResultadoLoteSeringas;
  onNovaEntrada: () => void;
  onSair: () => void;
}) {
  const [erro, setErro] = useState<string | null>(null);
  const codigos = lote.criadas.map((c) => c.codigo);

  return (
    <div className="ser-bipagem">
      <div className="ser-cabecalho">
        <div className="ser-cab-txt">
          <h3>{lote.quantidade} seringa(s) cadastrada(s)</h3>
          <p className="muted">
            Da <strong>{lote.primeiro}</strong> à <strong>{lote.ultimo}</strong> — já estão no
            estoque. Agora imprima as etiquetas e cole em cada uma.
          </p>
        </div>
        <span className="badge">{lote.total} em estoque</span>
      </div>

      <div className="ser-etiq-acoes">
        <button
          className="btn-primary btn-grande"
          onClick={() => abrirEtiquetas(codigos, `Etiquetas ${lote.primeiro} a ${lote.ultimo}`, setErro)}
        >
          <IconDownload size={18} /> Imprimir as {lote.quantidade} etiquetas
        </button>
        <span className="muted">
          Abre numa aba nova, já formatado: Ctrl+P e “Salvar como PDF” ou direto na impressora de
          etiquetas. Bipe uma para conferir antes de imprimir a folha toda.
        </span>
      </div>

      {erro && <div className="conf-aviso erro">{erro}</div>}

      <div className="conf-lista">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Código</th>
              <th>Etiqueta</th>
            </tr>
          </thead>
          <tbody>
            {lote.criadas.map((c, n) => (
              <tr key={c.id}>
                <td>{n + 1}</td>
                <td>
                  <strong>{c.codigo}</strong>
                </td>
                <td>
                  {/* O desenho de verdade, para conferir antes de imprimir. */}
                  <span
                    className="ser-barras"
                    dangerouslySetInnerHTML={{
                      __html: code128SVG(c.codigo, { modulo: 1, altura: 26, legenda: false }),
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ser-rodape">
        <button className="btn-secondary" onClick={onNovaEntrada}>
          <IconPlus size={16} /> Cadastrar outra caixa
        </button>
        <button className="btn-secondary" onClick={onSair}>
          Voltar
        </button>
      </div>
    </div>
  );
}

/**
 * Abre a folha de etiquetas numa aba nova.
 *
 * A janela é aberta ANTES de montar o HTML de propósito: aberta depois de uma
 * espera, o navegador a trata como pop-up não pedido pela pessoa e bloqueia —
 * o mesmo cuidado dos documentos dos Correios.
 */
function abrirEtiquetas(codigos: string[], titulo: string, onErro: (m: string) => void) {
  const janela = window.open("", "_blank");
  if (!janela) {
    onErro("O navegador bloqueou a janela. Libere os pop-ups para este site e tente de novo.");
    return;
  }
  janela.document.open();
  janela.document.write(htmlEtiquetas(codigos, titulo));
  janela.document.close();
  janela.focus();
}

/* ======================================================================
 *  SUBABA 2 — ESTOQUE
 * ==================================================================== */

function EstoqueSeringas({
  onMudou,
  resumo,
}: {
  onMudou: () => void;
  resumo: ResumoSeringas | null;
}) {
  const podeExcluir = usePodeExcluir();
  const [busca, setBusca] = useState("");
  const [situacao, setSituacao] = useState("");
  const [linhas, setLinhas] = useState<Row[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.seringas.estoque({ q: busca.trim(), situacao });
      // A→Z pelo código (ver src/ordem.ts): a lista é procurada pelo número da
      // seringa, e ordem do banco não é ordem de quem procura.
      setLinhas([...r.seringas].sort((a, b) => compararNomes(a.codigo, b.codigo)));
      setErro(null);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Erro ao carregar o estoque.");
    } finally {
      setCarregando(false);
    }
  }, [busca, situacao]);

  useEffect(() => {
    const t = setTimeout(() => void carregar(), 250);
    return () => clearTimeout(t);
  }, [carregar]);

  async function darBaixa(row: Row) {
    const motivo = prompt(
      `Dar baixa na seringa ${row.codigo}?\n\nEscreva o motivo (quebrou, vazou, perdeu…):`,
      "",
    );
    if (motivo === null) return;
    try {
      await api.seringas.atualizar(Number(row.id), {
        situacao: "Baixada",
        observacao: motivo.trim() || undefined,
      });
      await carregar();
      onMudou();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Erro ao dar baixa.");
    }
  }

  async function voltarAoEstoque(row: Row) {
    try {
      await api.seringas.atualizar(Number(row.id), { situacao: "Em estoque" });
      await carregar();
      onMudou();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Erro ao devolver ao estoque.");
    }
  }

  async function excluir(row: Row) {
    if (!confirm(`Excluir a seringa ${row.codigo} do cadastro?`)) return;
    try {
      await api.seringas.remover(Number(row.id));
      await carregar();
      onMudou();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Erro ao excluir.");
    }
  }

  return (
    <>
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={16} />
          <input
            className="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Procure pelo código, tipo, OS ou cliente"
          />
        </div>
        <div className="crud-filtros">
          <label className="crud-filtro">
            <span>Situação</span>
            <select
              className="crud-select"
              value={situacao}
              onChange={(e) => setSituacao(e.target.value)}
            >
              <option value="">Todas</option>
              {SITUACOES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="toolbar-right">
          <span className="badge">{linhas.length} na lista</span>
        </div>
      </div>

      {/*
        A QUANTIDADE, grande, antes da tabela. É a primeira pergunta de quem
        abre o estoque ("quantas eu tenho?") e ela estava escondida num badge
        cinza no canto, do tamanho de um rodapé.
      */}
      <div className="ser-quadro">
        <div className="ser-quadro-item destaque">
          <span className="ser-quadro-num">{resumo?.estoque.emEstoque ?? 0}</span>
          <span className="ser-quadro-rot">em estoque</span>
        </div>
        <div className="ser-quadro-item">
          <span className="ser-quadro-num">{resumo?.estoque.emServico ?? 0}</span>
          <span className="ser-quadro-rot">em serviço</span>
        </div>
        <div className="ser-quadro-item">
          <span className="ser-quadro-num">{resumo?.estoque.comCliente ?? 0}</span>
          <span className="ser-quadro-rot">com o cliente</span>
        </div>
        <div className="ser-quadro-item">
          <span className="ser-quadro-num">{resumo?.estoque.emLabExterno ?? 0}</span>
          <span className="ser-quadro-rot">em lab. externo</span>
        </div>
        <div className="ser-quadro-item">
          <span className="ser-quadro-num">{resumo?.estoque.faltantes ?? 0}</span>
          <span className="ser-quadro-rot">faltantes</span>
        </div>
        <div className="ser-quadro-item">
          <span className="ser-quadro-num">{resumo?.estoque.baixadas ?? 0}</span>
          <span className="ser-quadro-rot">baixadas</span>
        </div>
        <div className="ser-quadro-item total">
          <span className="ser-quadro-num">{resumo?.estoque.total ?? 0}</span>
          <span className="ser-quadro-rot">no cadastro</span>
        </div>
      </div>

      <div className="table-wrap">
        {carregando ? (
          <div className="empty">Carregando...</div>
        ) : erro ? (
          <div className="empty">{erro}</div>
        ) : linhas.length === 0 ? (
          <div className="empty">
            Nenhuma seringa aqui. Cadastre na subaba <strong>Cadastro</strong>, bipando a caixa.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Tipo</th>
                <th>Situação</th>
                <th>Onde está</th>
                <th>Desde</th>
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((s) => (
                <tr key={String(s.id)}>
                  <td>
                    <strong>{String(s.codigo)}</strong>
                  </td>
                  <td>{String(s.tipo ?? "—")}</td>
                  <td>
                    <span className={`pill ${tomDaSituacao(String(s.situacao))}`}>
                      {String(s.situacao)}
                    </span>
                  </td>
                  <td>
                    {ondeEsta(s)}
                    {s.observacao &&
                      (String(s.situacao) === "Baixada" || String(s.situacao) === "Faltante") && (
                      <div className="an-nota">{String(s.observacao)}</div>
                    )}
                  </td>
                  <td>{formatDate(s.atualizada_em ?? s.created_at)}</td>
                  <td className="col-actions">
                    {/*
                      "Em serviço", "Com cliente" e "Em laboratório externo" NÃO têm botão de voltar: a
                      seringa que está fora volta pela bipagem no Retorno,
                      senão o lote ficaria esperando para sempre uma seringa
                      que alguém devolveu no clique.
                    */}
                    {/* A faltante também pode ser baixada: é quando se desiste
                        de recuperá-la. Se ela aparecer, volta pelo Retorno. */}
                    {(String(s.situacao) === "Em estoque" || String(s.situacao) === "Faltante") && (
                      <button
                        className="icon-btn"
                        title="Dar baixa (quebrou, vazou, perdeu)"
                        onClick={() => void darBaixa(s)}
                      >
                        <IconAlert size={16} />
                      </button>
                    )}
                    {String(s.situacao) === "Baixada" && (
                      <button
                        className="icon-btn icon-btn-brand"
                        title="Voltar para o estoque"
                        onClick={() => void voltarAoEstoque(s)}
                      >
                        <IconCheck size={16} />
                      </button>
                    )}
                    {/* Etiqueta descolou, borrou, a seringa foi para o campo e
                        voltou ilegível: reimprimir a dela, sozinha. */}
                    <button
                      className="icon-btn"
                      title="Imprimir a etiqueta desta seringa"
                      onClick={() =>
                        abrirEtiquetas(
                          [String(s.codigo)],
                          `Etiqueta ${s.codigo}`,
                          (m) => alert(m),
                        )
                      }
                    >
                      <IconDownload size={16} />
                    </button>
                    {podeExcluir && !s.saida_id && (
                      <button
                        className="icon-btn icon-btn-danger"
                        title="Excluir do cadastro"
                        onClick={() => void excluir(s)}
                      >
                        <IconTrash size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/* ======================================================================
 *  BUSCA DE CLIENTE — nome ou CNPJ, com o cadastro que o sistema já tem
 * ==================================================================== */

function BuscaCliente({
  nome,
  cnpj,
  onEscolher,
}: {
  nome: string;
  cnpj: string;
  onEscolher: (nome: string, cnpj: string) => void;
}) {
  const [achados, setAchados] = useState<ClienteSeringa[]>([]);
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    const termo = nome.trim();
    if (termo.length < 2) {
      setAchados([]);
      return;
    }
    const controller = new AbortController();
    const t = setTimeout(() => {
      api.seringas
        .clientes(termo, controller.signal)
        .then((r) => {
          setAchados(r.clientes.slice(0, 8));
          setAberto(true);
        })
        .catch(() => setAchados([]));
    }, 300);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [nome]);

  return (
    <div className="ser-form-linha">
      <label className="ser-campo ser-campo-busca">
        <span>Cliente</span>
        <input
          value={nome}
          onChange={(e) => onEscolher(e.target.value, cnpj)}
          placeholder="digite o nome ou o CNPJ e escolha na lista"
          autoComplete="off"
        />
        {aberto && achados.length > 0 && (
          <div className="ser-sugestoes">
            {achados.map((c) => (
              <button
                key={c.nome}
                type="button"
                className="ser-sugestao"
                onClick={() => {
                  onEscolher(c.nome, c.cnpj ?? cnpj);
                  setAberto(false);
                }}
              >
                <strong>{c.nome}</strong>
                <span className="muted">
                  {c.cnpj ? c.cnpj : "sem CNPJ no cadastro"} · {c.fontes.join(", ")}
                </span>
              </button>
            ))}
          </div>
        )}
      </label>
      <label className="ser-campo">
        <span>CNPJ</span>
        <input
          value={cnpj}
          onChange={(e) => onEscolher(nome, e.target.value)}
          placeholder="00.000.000/0000-00"
          autoComplete="off"
        />
      </label>
    </div>
  );
}

/* ======================================================================
 *  SUBABA 3 — SAÍDA
 * ==================================================================== */

function SaidaSeringas({
  saidaAberta,
  onAbrirSaida,
  onMudou,
}: {
  saidaAberta: number | null;
  onAbrirSaida: (id: number | null) => void;
  onMudou: () => void;
}) {
  const podeExcluir = usePodeExcluir();
  const [saidas, setSaidas] = useState<Row[]>([]);
  const [busca, setBusca] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [criando, setCriando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.seringas.saidas({ q: busca.trim() });
      setSaidas(r.saidas);
    } catch {
      setSaidas([]);
    } finally {
      setCarregando(false);
    }
  }, [busca]);

  useEffect(() => {
    if (saidaAberta) return;
    const t = setTimeout(() => void carregar(), 250);
    return () => clearTimeout(t);
  }, [carregar, saidaAberta]);

  async function excluirSaida(row: Row) {
    if (
      !confirm(
        `Apagar a saída #${row.id}?\n\nAs seringas dela voltam para o estoque. ` +
          "Para uma saída que já circulou, o certo é encerrar pelo Retorno.",
      )
    )
      return;
    try {
      await api.seringas.removerSaida(Number(row.id));
      await carregar();
      onMudou();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Erro ao apagar a saída.");
    }
  }

  if (saidaAberta) {
    return (
      <BipagemSaida
        id={saidaAberta}
        onFechar={() => {
          onAbrirSaida(null);
          void carregar();
        }}
        onMudou={onMudou}
      />
    );
  }

  return (
    <>
      {criando && (
        <NovaSaida
          onCancelar={() => setCriando(false)}
          onCriada={(id) => {
            setCriando(false);
            onMudou();
            onAbrirSaida(id);
          }}
        />
      )}

      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={16} />
          <input
            className="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Procure pela OS, pelo cliente ou pelo CNPJ"
          />
        </div>
        <div className="toolbar-right">
          <span className="badge">{saidas.length} saída(s)</span>
          <button className="btn-primary" onClick={() => setCriando(true)}>
            <IconPlus size={16} /> Nova saída
          </button>
        </div>
      </div>

      <div className="table-wrap">
        {carregando ? (
          <div className="empty">Carregando...</div>
        ) : saidas.length === 0 ? (
          <div className="empty">
            Nenhuma saída registrada. Clique em <strong>Nova saída</strong> para mandar seringas
            para uma OS ou para um cliente.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Data</th>
                <th>Destino</th>
                <th>OS / Cliente</th>
                <th>Seringas</th>
                <th>Situação</th>
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {saidas.map((x) => {
                const bipadas = Number(x.bipadas) || 0;
                const retornadas = Number(x.retornadas) || 0;
                const previsto = Number(x.quantidade_prevista) || 0;
                return (
                  <tr
                    key={String(x.id)}
                    className="row-clickable"
                    onDoubleClick={() => onAbrirSaida(Number(x.id))}
                  >
                    <td>{String(x.id)}</td>
                    <td>{formatDate(x.data)}</td>
                    <td>
                      {x.destino === LAB_EXTERNO ? (
                        <span className="an-selos">
                          <IconFlask size={14} /> Lab. externo
                        </span>
                      ) : x.destino === "Cliente" ? (
                        <span className="an-selos">
                          <IconTruck size={14} /> Cliente
                        </span>
                      ) : (
                        <span className="an-selos">
                          <IconBox size={14} /> Serviço
                        </span>
                      )}
                    </td>
                    <td>
                      {x.destino === LAB_EXTERNO ? (
                        <>
                          <strong>{String(x.laboratorio_externo ?? "—")}</strong>
                          {x.os_numero && <div className="an-nota">OS {String(x.os_numero)}</div>}
                        </>
                      ) : x.destino === "Cliente" ? (
                        <>
                          <strong>{String(x.cliente_nome ?? "—")}</strong>
                          {x.cliente_cnpj && <div className="an-nota">{String(x.cliente_cnpj)}</div>}
                        </>
                      ) : (
                        <strong>OS {String(x.os_numero ?? "—")}</strong>
                      )}
                    </td>
                    <td>
                      {bipadas}
                      {previsto > 0 && ` de ${previsto}`}
                      {String(x.status) !== "Em separação" && (
                        <div className="an-nota">{retornadas} já voltaram</div>
                      )}
                    </td>
                    <td>
                      <span
                        className={`pill ${
                          x.status === "Concluída"
                            ? "pill-ok"
                            : x.status === "Enviada"
                              ? "pill-warn"
                              : "pill-neutral"
                        }`}
                      >
                        {String(x.status)}
                      </span>
                    </td>
                    <td className="col-actions">
                      <button
                        className="icon-btn icon-btn-brand"
                        title="Abrir a saída"
                        onClick={() => onAbrirSaida(Number(x.id))}
                      >
                        <IconSearch size={16} />
                      </button>
                      {podeExcluir && (
                        <button
                          className="icon-btn icon-btn-danger"
                          title="Apagar a saída"
                          onClick={() => void excluirSaida(x)}
                        >
                          <IconTrash size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/**
 * NOVA SAÍDA — o pop-up que abre o lote.
 *
 * Fica DENTRO da subaba (pop-up), e não numa aba nova: é uma etapa do mesmo
 * trabalho. As perguntas mudam com o destino porque as obrigações são
 * diferentes — saída de serviço precisa de OS, saída para cliente precisa de
 * quem vai receber, e o envio para laboratório externo precisa do nome do
 * laboratório (é de quem se cobra a volta).
 */
function NovaSaida({
  onCancelar,
  onCriada,
}: {
  onCancelar: () => void;
  onCriada: (id: number) => void;
}) {
  const [destino, setDestino] = useState<"Serviço" | "Cliente" | typeof LAB_EXTERNO>("Serviço");
  const [os, setOs] = useState("");
  const [labExterno, setLabExterno] = useState("");
  const [labsConhecidos, setLabsConhecidos] = useState<string[]>([]);

  // Os laboratórios já usados viram sugestão — evita "Lab X" e "LAB X LTDA"
  // virarem dois destinos diferentes.
  useEffect(() => {
    if (destino !== LAB_EXTERNO || labsConhecidos.length) return;
    api.seringas
      .laboratoriosExternos()
      .then((r) => setLabsConhecidos(r.laboratorios))
      .catch(() => {});
  }, [destino, labsConhecidos.length]);
  const [clienteNome, setClienteNome] = useState("");
  const [clienteCnpj, setClienteCnpj] = useState("");
  const [data, setData] = useState(hojeISO());
  const [quantidade, setQuantidade] = useState("");
  const [observacao, setObservacao] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function salvar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    // É o teto da bipagem: lançou 10, saem no máximo 10.
    if (!(Number(quantidade) >= 1)) {
      setErro("Informe quantas seringas vão sair.");
      return;
    }
    if (destino === LAB_EXTERNO && !labExterno.trim()) {
      setErro("Informe o laboratório externo que vai receber as seringas.");
      return;
    }
    setSalvando(true);
    try {
      const r = await api.seringas.criarSaida({
        destino,
        os_numero: os.trim(),
        cliente_nome: clienteNome.trim(),
        cliente_cnpj: clienteCnpj.trim(),
        laboratorio_externo: destino === LAB_EXTERNO ? labExterno.trim() : "",
        data,
        quantidade_prevista: Number(quantidade) || 0,
        observacao: observacao.trim(),
      });
      onCriada(Number(r.saida.id));
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Erro ao abrir a saída.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancelar}>
      <form
        className="modal-card modal-wide"
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={salvar}
      >
        <div className="modal-head rf-head">
          <div className="rf-head-txt">
            <span className="rf-head-icon">
              <IconTruck size={20} />
            </span>
            <div>
              <h3>Nova saída de seringa</h3>
              <p className="rf-head-sub">
                Diga para onde vão e quantas são — a bipagem abre em seguida.
              </p>
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onCancelar} title="Fechar">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body ser-form">
          <div className="ser-escolha">
            <span className="ser-escolha-rot">Para onde vai</span>
            <div className="ser-escolha-botoes">
              <button
                type="button"
                className={`ser-chip ${destino === "Serviço" ? "ativo" : ""}`}
                onClick={() => setDestino("Serviço")}
              >
                <IconBox size={16} /> Coleta Sercamp (OS)
              </button>
              <button
                type="button"
                className={`ser-chip ${destino === "Cliente" ? "ativo" : ""}`}
                onClick={() => setDestino("Cliente")}
              >
                <IconTruck size={16} /> Envio para o cliente
              </button>
              <button
                type="button"
                className={`ser-chip ${destino === LAB_EXTERNO ? "ativo" : ""}`}
                onClick={() => setDestino(LAB_EXTERNO)}
              >
                <IconFlask size={16} /> Envio para laboratório externo
              </button>
            </div>
          </div>

          {destino === LAB_EXTERNO ? (
            <div className="ser-form-linha">
              <label className="ser-campo">
                <span>Laboratório externo</span>
                <input
                  value={labExterno}
                  onChange={(e) => setLabExterno(e.target.value)}
                  placeholder="nome do laboratório que vai receber"
                  list="ser-labs-externos"
                  autoFocus
                  required
                />
                <datalist id="ser-labs-externos">
                  {labsConhecidos.map((l) => (
                    <option key={l} value={l} />
                  ))}
                </datalist>
              </label>
              <label className="ser-campo">
                <span>OS (opcional)</span>
                <input
                  value={os}
                  onChange={(e) => setOs(e.target.value)}
                  placeholder="se as amostras são de uma OS"
                />
              </label>
            </div>
          ) : destino === "Serviço" ? (
            <div className="ser-form-linha">
              <label className="ser-campo">
                <span>OS do serviço</span>
                <input
                  value={os}
                  onChange={(e) => setOs(e.target.value)}
                  placeholder="número da ordem de serviço"
                  autoFocus
                />
              </label>
              <label className="ser-campo">
                <span>Cliente (opcional)</span>
                <input
                  value={clienteNome}
                  onChange={(e) => setClienteNome(e.target.value)}
                  placeholder="de quem é o serviço"
                />
              </label>
            </div>
          ) : (
            <>
              <BuscaCliente
                nome={clienteNome}
                cnpj={clienteCnpj}
                onEscolher={(n, c) => {
                  setClienteNome(n);
                  setClienteCnpj(c);
                }}
              />
              <div className="ser-form-linha">
                <label className="ser-campo">
                  <span>OS (opcional)</span>
                  <input
                    value={os}
                    onChange={(e) => setOs(e.target.value)}
                    placeholder="se o envio já tem OS"
                  />
                </label>
              </div>
            </>
          )}

          <div className="ser-form-linha">
            <label className="ser-campo">
              <span>Data</span>
              <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
            </label>
            <label className="ser-campo">
              <span>Quantidade de seringas</span>
              <input
                type="number"
                min={1}
                step={1}
                required
                value={quantidade}
                onChange={(e) => setQuantidade(e.target.value)}
                placeholder="quantas vão sair"
              />
            </label>
          </div>

          <label className="ser-campo">
            <span>Observação</span>
            <input
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="opcional"
            />
          </label>

          <p className="muted">
            A quantidade é o limite da bipagem: depois de salvar, a área de leitura abre e aceita
            só essa quantidade de seringas.
          </p>

          {erro && <div className="conf-aviso erro">{erro}</div>}
        </div>

        <div className="modal-foot">
          <button type="submit" className="btn-primary" disabled={salvando}>
            <IconCheck size={16} /> Abrir a bipagem
          </button>
          <button type="button" className="btn-secondary" onClick={onCancelar}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

/** A tela de bipar a saída: contador, leitor, lista e o botão de confirmar. */
function BipagemSaida({
  id,
  onFechar,
  onMudou,
}: {
  id: number;
  onFechar: () => void;
  onMudou: () => void;
}) {
  const [dados, setDados] = useState<DetalheSaidaSeringas | null>(null);
  const [codigo, setCodigo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<Aviso>(null);
  const [camAberta, setCamAberta] = useState(false);
  /** Fica true quando o servidor pediu confirmação para sair com divergência. */
  const [confirmarDif, setConfirmarDif] = useState(false);

  const campoRef = useRef<HTMLInputElement>(null);
  const focar = useCallback(() => {
    if (!camAberta) setTimeout(() => campoRef.current?.focus(), 0);
  }, [camAberta]);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.seringas.saida(id));
    } catch (err) {
      setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao abrir a saída." });
    } finally {
      focar();
    }
  }, [id, focar]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const registrar = useCallback(
    async (bruto: string, origem: "leitor" | "camera"): Promise<"novo" | "duplicado" | "erro"> => {
      const valor = bruto.trim();
      if (!valor) return "erro";
      try {
        const r = await api.seringas.biparSaida(id, valor, origem);
        if (r.desconhecida) {
          setAviso({
            tipo: "erro",
            texto: `${r.mensagem} Cadastre-a na subaba “Cadastro” e bipe de novo.`,
          });
          return "erro";
        }
        if (r.duplicada) {
          setAviso({ tipo: "atencao", texto: r.mensagem ?? `"${valor}" já está nesta saída.` });
          return "duplicado";
        }
        setAviso({
          tipo: "ok",
          texto: r.completo
            ? `${valor} separada — a conta fechou (${r.bipadas} de ${r.previsto}).`
            : `${valor} separada.`,
        });
        // Completou: a câmera para, não há mais vaga (ver o teto no servidor).
        if (r.completo) setCamAberta(false);
        await carregar();
        onMudou();
        return "novo";
      } catch (err) {
        setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao bipar." });
        return "erro";
      }
    },
    [id, carregar, onMudou],
  );

  async function bipar(e: React.FormEvent) {
    e.preventDefault();
    const valor = codigo.trim();
    if (!valor || ocupado) return;
    setOcupado(true);
    try {
      await registrar(valor, "leitor");
    } finally {
      // Limpa até depois de erro: o leitor digita por cima do que está no campo
      // e a leitura seguinte grudaria na errada. O código lido fica no aviso.
      setCodigo("");
      setOcupado(false);
      focar();
    }
  }

  /** Tirar da lista — aberto a quem bipa (não só ao admin): é o desfazer da bancada. */
  async function remover(item: Row) {
    if (!confirm(`Tirar "${item.codigo}" desta saída? Ela volta para o estoque.`)) return;
    try {
      await api.seringas.removerItemSaida(id, Number(item.id));
      setAviso({ tipo: "atencao", texto: `"${item.codigo}" voltou para o estoque.` });
      await carregar();
      onMudou();
    } catch (err) {
      setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao remover." });
    }
  }

  async function confirmar(forcar: boolean) {
    setOcupado(true);
    try {
      const r = await api.seringas.confirmarSaida(id, forcar);
      if (r?.divergencia) {
        setConfirmarDif(true);
        setAviso({ tipo: "atencao", texto: r.mensagem });
        return;
      }
      setConfirmarDif(false);
      setAviso({ tipo: "ok", texto: r?.mensagem ?? "Saída registrada." });
      await carregar();
      onMudou();
    } catch (err) {
      setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao confirmar." });
    } finally {
      setOcupado(false);
    }
  }

  const itens = dados?.itens ?? [];
  const jaLidos = useMemo(
    () => new Set(itens.map((i) => chaveDoCodigo(String(i.codigo)))),
    [itens],
  );

  if (!dados) {
    return <div className="empty">Carregando...</div>;
  }

  const saida = dados.saida;
  const emSeparacao = String(saida.status) === "Em separação";
  /** Bipou tudo o que foi lançado: o leitor some até alguém tirar uma da lista. */
  const lotada = dados.previsto > 0 && dados.bipadas >= dados.previsto;

  return (
    <div className="ser-bipagem" onClick={focar}>
      <div className="ser-cabecalho">
        <div className="ser-cab-txt">
          <h3>
            {saida.destino === LAB_EXTERNO
              ? `Envio para laboratório externo — ${saida.laboratorio_externo ?? "não informado"}`
              : saida.destino === "Cliente"
                ? `Envio para ${saida.cliente_nome ?? "cliente não informado"}`
                : `Coleta Sercamp — OS ${saida.os_numero ?? "não informada"}`}
          </h3>
          <p className="muted">
            {formatDate(saida.data)}
            {saida.cliente_cnpj ? ` · CNPJ ${saida.cliente_cnpj}` : ""}
            {saida.destino !== "Serviço" && saida.os_numero ? ` · OS ${saida.os_numero}` : ""}
            {saida.observacao ? ` · ${saida.observacao}` : ""}
          </p>
        </div>
        <span
          className={`pill ${
            saida.status === "Concluída"
              ? "pill-ok"
              : saida.status === "Enviada"
                ? "pill-warn"
                : "pill-neutral"
          }`}
        >
          {String(saida.status)}
        </span>
      </div>

      <Contador
        feito={dados.bipadas}
        previsto={dados.previsto}
        rotuloFeito="bipadas"
        rotuloPrevisto="previstas"
      />

      {emSeparacao && lotada ? (
        <div className="conf-aviso ok">
          As {dados.previsto} seringa(s) lançadas já foram bipadas. Confirme a saída — ou tire uma
          da lista para bipar outra no lugar.
        </div>
      ) : emSeparacao ? (
        <BarraLeitor
          campoRef={campoRef}
          codigo={codigo}
          setCodigo={setCodigo}
          onBipar={bipar}
          ocupado={ocupado}
          camAberta={camAberta}
          onAbrirCamera={() => setCamAberta(true)}
          rotulo="Bipe as seringas que vão sair (ou digite e pressione Enter)"
        />
      ) : (
        <div className="conf-aviso atencao">
          Esta saída já foi confirmada — {dados.retornadas} de {dados.bipadas} já voltaram. O que
          falta se resolve na subaba <strong>Retorno</strong>.
        </div>
      )}

      {camAberta && emSeparacao && !lotada && (
        <CameraScanner
          jaConferidos={jaLidos}
          onCodigo={(texto) => registrar(texto, "camera")}
          onFechar={() => {
            setCamAberta(false);
            void carregar();
          }}
          onDigitar={() => {
            setCamAberta(false);
            void carregar();
          }}
        />
      )}

      {aviso && <div className={`conf-aviso ${aviso.tipo}`}>{aviso.texto}</div>}

      <div className="conf-lista">
        {itens.length === 0 ? (
          <div className="empty">Nenhuma seringa nesta saída ainda — bipe a primeira.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Código</th>
                <th>Tipo</th>
                <th>Bipada por</th>
                <th>Situação</th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {itens.map((i, n) => (
                <tr key={String(i.id)}>
                  <td>{itens.length - n}</td>
                  <td>
                    <strong>{String(i.codigo)}</strong>
                  </td>
                  <td>{String(i.tipo ?? "—")}</td>
                  <td>
                    {String(i.bipada_por ?? "—")}
                    {i.origem === "camera" && <span className="conf-tag-camera">câmera</span>}
                  </td>
                  <td>
                    {i.retornada_em ? (
                      <span className="pill pill-ok">Voltou</span>
                    ) : (
                      <span className={`pill ${tomDaSituacao(String(i.situacao))}`}>
                        {String(i.situacao)}
                      </span>
                    )}
                  </td>
                  <td className="col-actions">
                    {emSeparacao && (
                      <button
                        className="icon-btn icon-btn-danger"
                        title="Tirar esta seringa da saída"
                        onClick={(e) => {
                          e.stopPropagation();
                          void remover(i);
                        }}
                      >
                        <IconTrash size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="ser-rodape">
        {emSeparacao && (
          <button
            className="btn-primary"
            disabled={ocupado || dados.bipadas === 0}
            onClick={() => void confirmar(confirmarDif)}
          >
            <IconSend size={16} />{" "}
            {confirmarDif ? "Confirmar mesmo com a diferença" : "Confirmar a saída"}
          </button>
        )}
        <button className="btn-secondary" onClick={onFechar}>
          Voltar para a lista
        </button>
      </div>
    </div>
  );
}

/* ======================================================================
 *  SUBABA 4 — RETORNO
 * ==================================================================== */

/**
 * O RETORNO começa pela LISTA DO QUE ESTÁ FORA — e não por um campo de leitor
 * solto. É assim porque a pergunta de quem recebe a caixa de volta é sempre
 * "de quem é isto?": ela procura pela OS ou pelo cliente, abre a ordem e bipa.
 * Um leitor que aceitasse qualquer código sem ordem aberta devolveria a seringa
 * ao estoque sem ninguém saber de qual saída ela veio.
 */
function RetornoSeringas({
  saidaAberta,
  onAbrirSaida,
  onMudou,
}: {
  saidaAberta: number | null;
  onAbrirSaida: (id: number | null) => void;
  onMudou: () => void;
}) {
  const [saidas, setSaidas] = useState<Row[]>([]);
  const [busca, setBusca] = useState("");
  const [status, setStatus] = useState("Enviada");
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.seringas.saidas({ q: busca.trim(), status });
      setSaidas(r.saidas);
    } catch {
      setSaidas([]);
    } finally {
      setCarregando(false);
    }
  }, [busca, status]);

  useEffect(() => {
    if (saidaAberta) return;
    const t = setTimeout(() => void carregar(), 250);
    return () => clearTimeout(t);
  }, [carregar, saidaAberta]);

  if (saidaAberta) {
    return (
      <BipagemRetorno
        id={saidaAberta}
        onFechar={() => {
          onAbrirSaida(null);
          void carregar();
        }}
        onMudou={onMudou}
      />
    );
  }

  return (
    <>
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={16} />
          <input
            className="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Procure pela OS ou pelo cliente"
          />
        </div>
        <div className="crud-filtros">
          <label className="crud-filtro">
            <span>Mostrar</span>
            <select className="crud-select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="Enviada">Aguardando retorno</option>
              <option value="Concluída">Já encerradas</option>
              <option value="">Todas</option>
            </select>
          </label>
        </div>
        <div className="toolbar-right">
          <span className="badge">{saidas.length} saída(s)</span>
        </div>
      </div>

      <div className="table-wrap">
        {carregando ? (
          <div className="empty">Carregando...</div>
        ) : saidas.length === 0 ? (
          <div className="empty">
            {status === "Enviada"
              ? "Nenhuma seringa está fora no momento — nada para receber de volta."
              : "Nenhuma saída nesta situação."}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Data</th>
                <th>OS / Cliente</th>
                <th>Voltaram</th>
                <th>Faltam / faltantes</th>
                <th>Situação</th>
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {saidas.map((x) => {
                const bipadas = Number(x.bipadas) || 0;
                const retornadas = Number(x.retornadas) || 0;
                const faltam = bipadas - retornadas;
                return (
                  <tr
                    key={String(x.id)}
                    className="row-clickable"
                    onDoubleClick={() => onAbrirSaida(Number(x.id))}
                  >
                    <td>{String(x.id)}</td>
                    <td>{formatDate(x.data)}</td>
                    <td>
                      {x.destino === LAB_EXTERNO ? (
                        <>
                          <strong>{String(x.laboratorio_externo ?? "—")}</strong>
                          <div className="an-nota">
                            Laboratório externo{x.os_numero ? ` · OS ${String(x.os_numero)}` : ""}
                          </div>
                        </>
                      ) : x.destino === "Cliente" ? (
                        <>
                          <strong>{String(x.cliente_nome ?? "—")}</strong>
                          {x.cliente_cnpj && <div className="an-nota">{String(x.cliente_cnpj)}</div>}
                        </>
                      ) : (
                        <strong>OS {String(x.os_numero ?? "—")}</strong>
                      )}
                    </td>
                    <td>
                      {retornadas} de {bipadas}
                    </td>
                    <td>
                      {faltam <= 0 ? (
                        "—"
                      ) : x.status === "Concluída" ? (
                        // Encerrada com menos: o que não voltou ficou FALTANTE.
                        <span className="pill pill-warn">{faltam} faltante(s)</span>
                      ) : (
                        <strong>{faltam}</strong>
                      )}
                    </td>
                    <td>
                      <span className={`pill ${x.status === "Concluída" ? "pill-ok" : "pill-warn"}`}>
                        {String(x.status)}
                      </span>
                    </td>
                    <td className="col-actions">
                      <button
                        className="icon-btn icon-btn-brand"
                        title="Abrir e bipar o retorno"
                        onClick={() => onAbrirSaida(Number(x.id))}
                      >
                        <IconSearch size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/** A bipagem do retorno: o que falta voltar fica no topo da lista. */
function BipagemRetorno({
  id,
  onFechar,
  onMudou,
}: {
  id: number;
  onFechar: () => void;
  onMudou: () => void;
}) {
  const [dados, setDados] = useState<DetalheSaidaSeringas | null>(null);
  const [codigo, setCodigo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<Aviso>(null);
  const [camAberta, setCamAberta] = useState(false);

  const campoRef = useRef<HTMLInputElement>(null);
  const focar = useCallback(() => {
    if (!camAberta) setTimeout(() => campoRef.current?.focus(), 0);
  }, [camAberta]);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.seringas.saida(id));
    } catch (err) {
      setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao abrir a saída." });
    } finally {
      focar();
    }
  }, [id, focar]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const registrar = useCallback(
    async (bruto: string, origem: "leitor" | "camera"): Promise<"novo" | "duplicado" | "erro"> => {
      const valor = bruto.trim();
      if (!valor) return "erro";
      try {
        const r = await api.seringas.retornar(id, valor, origem);
        if (r.duplicada) {
          setAviso({ tipo: "atencao", texto: r.mensagem ?? `"${valor}" já foi devolvida.` });
          return "duplicado";
        }
        setAviso({ tipo: "ok", texto: r.mensagem ?? `${valor} devolvida ao estoque.` });
        await carregar();
        onMudou();
        return "novo";
      } catch (err) {
        setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao registrar o retorno." });
        return "erro";
      }
    },
    [id, carregar, onMudou],
  );

  async function bipar(e: React.FormEvent) {
    e.preventDefault();
    const valor = codigo.trim();
    if (!valor || ocupado) return;
    setOcupado(true);
    try {
      await registrar(valor, "leitor");
    } finally {
      // Limpa até depois de erro: o leitor digita por cima do que está no campo
      // e a leitura seguinte grudaria na errada. O código lido fica no aviso.
      setCodigo("");
      setOcupado(false);
      focar();
    }
  }

  /**
   * O cliente devolveu MENOS do que levou: encerrar marca as que não voltaram
   * como FALTANTE (continuam presas a esta saída, para cobrar). Por isso o
   * servidor exige a segunda confirmação e o motivo é pedido aqui.
   */
  async function encerrar() {
    setOcupado(true);
    try {
      let r = await api.seringas.concluirSaida(id, false);
      if (r?.pendencia) {
        if (!confirm(`${r.mensagem}`)) return;
        const motivo = prompt("Observação (fica escrita na ficha de cada seringa faltante):", "");
        if (motivo === null) return;
        r = await api.seringas.concluirSaida(id, true, motivo);
      }
      setAviso({ tipo: "ok", texto: r?.mensagem ?? "Saída encerrada." });
      await carregar();
      onMudou();
    } catch (err) {
      setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao encerrar." });
    } finally {
      setOcupado(false);
    }
  }

  /** Tirar da lista a devolução bipada por engano — a seringa volta a constar como fora. */
  async function desfazerDevolucao(item: Row) {
    if (!confirm(`Desfazer a devolução de "${item.codigo}"?\n\nEla volta a constar como fora.`)) return;
    try {
      const r = await api.seringas.desfazerRetorno(id, Number(item.id));
      setAviso({ tipo: "atencao", texto: r.mensagem ?? `"${item.codigo}" voltou a constar como fora.` });
      await carregar();
      onMudou();
    } catch (err) {
      setAviso({ tipo: "erro", texto: err instanceof ApiError ? err.message : "Erro ao desfazer." });
    }
  }

  const itens = dados?.itens ?? [];
  /** O que ainda não voltou fica em cima: é o que a pessoa está procurando. */
  const ordenados = useMemo(
    () => [...itens].sort((a, b) => (a.retornada_em ? 1 : 0) - (b.retornada_em ? 1 : 0)),
    [itens],
  );
  const jaVoltaram = useMemo(
    () =>
      new Set(
        itens.filter((i) => i.retornada_em).map((i) => chaveDoCodigo(String(i.codigo))),
      ),
    [itens],
  );

  if (!dados) return <div className="empty">Carregando...</div>;

  const saida = dados.saida;
  const encerrada = String(saida.status) === "Concluída";
  /** Encerrada com faltantes: o leitor continua, para a faltante que aparecer depois. */
  const aceitaBipar = !encerrada || dados.pendentes > 0;

  return (
    <div className="ser-bipagem" onClick={focar}>
      <div className="ser-cabecalho">
        <div className="ser-cab-txt">
          <h3>
            {saida.destino === LAB_EXTERNO
              ? `Retorno do laboratório externo — ${saida.laboratorio_externo ?? "não informado"}`
              : saida.destino === "Cliente"
                ? `Retorno de ${saida.cliente_nome ?? "cliente não informado"}`
                : `Retorno da OS ${saida.os_numero ?? "não informada"}`}
          </h3>
          <p className="muted">
            Saída de {formatDate(saida.data)}
            {saida.cliente_cnpj ? ` · CNPJ ${saida.cliente_cnpj}` : ""} — bipe cada seringa que
            voltou; ela é devolvida ao estoque na hora.
          </p>
        </div>
        <span className={`pill ${encerrada ? "pill-ok" : "pill-warn"}`}>{String(saida.status)}</span>
      </div>

      <Contador
        feito={dados.retornadas}
        previsto={dados.bipadas}
        rotuloFeito="voltaram"
        rotuloPrevisto="saíram"
      />

      {encerrada && (
        <div className={`conf-aviso ${dados.pendentes > 0 ? "atencao" : "ok"}`}>
          {dados.pendentes > 0
            ? `Devolução encerrada com ${dados.pendentes} seringa(s) faltante(s). Se alguma aparecer, bipe aqui que ela volta ao estoque.`
            : "Esta saída já foi encerrada — todas voltaram."}
        </div>
      )}
      {aceitaBipar && (
        <BarraLeitor
          campoRef={campoRef}
          codigo={codigo}
          setCodigo={setCodigo}
          onBipar={bipar}
          ocupado={ocupado}
          camAberta={camAberta}
          onAbrirCamera={() => setCamAberta(true)}
          rotulo="Bipe as seringas que voltaram (ou digite e pressione Enter)"
        />
      )}

      {camAberta && aceitaBipar && (
        <CameraScanner
          jaConferidos={jaVoltaram}
          onCodigo={(texto) => registrar(texto, "camera")}
          onFechar={() => {
            setCamAberta(false);
            void carregar();
          }}
          onDigitar={() => {
            setCamAberta(false);
            void carregar();
          }}
        />
      )}

      {aviso && <div className={`conf-aviso ${aviso.tipo}`}>{aviso.texto}</div>}

      <div className="conf-lista">
        {ordenados.length === 0 ? (
          <div className="empty">Esta saída não tem seringas.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Tipo</th>
                <th>Situação</th>
                <th>Voltou em</th>
                <th>Recebida por</th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {ordenados.map((i) => (
                <tr key={String(i.id)}>
                  <td>
                    <strong>{String(i.codigo)}</strong>
                  </td>
                  <td>{String(i.tipo ?? "—")}</td>
                  <td>
                    {i.retornada_em ? (
                      <span className="pill pill-ok">Devolvida</span>
                    ) : String(i.situacao) === "Faltante" ? (
                      <span className="pill pill-warn">Faltante</span>
                    ) : encerrada ? (
                      <span className={`pill ${tomDaSituacao(String(i.situacao))}`}>
                        {String(i.situacao)}
                      </span>
                    ) : (
                      <span className="pill pill-neutral">Ainda fora</span>
                    )}
                  </td>
                  <td>{i.retornada_em ? formatDate(i.retornada_em) : "—"}</td>
                  <td>
                    {String(i.retornada_por ?? "—")}
                    {i.retorno_origem === "camera" && (
                      <span className="conf-tag-camera">câmera</span>
                    )}
                  </td>
                  <td className="col-actions">
                    {/* Desfazer só enquanto ela segue parada no estoque; o
                        servidor recusa se já saiu de novo. */}
                    {i.retornada_em && String(i.situacao) === "Em estoque" && (!encerrada || dados.pendentes === 0) && (
                      <button
                        className="icon-btn icon-btn-danger"
                        title="Tirar da lista (desfazer esta devolução)"
                        onClick={(e) => {
                          e.stopPropagation();
                          void desfazerDevolucao(i);
                        }}
                      >
                        <IconTrash size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="ser-rodape">
        {!encerrada && (
          <button className="btn-secondary" disabled={ocupado} onClick={() => void encerrar()}>
            <IconCheck size={16} />{" "}
            {dados.pendentes > 0
              ? `Finalizar devolução (${dados.pendentes} não voltaram)`
              : "Encerrar esta saída"}
          </button>
        )}
        <button className="btn-secondary" onClick={onFechar}>
          Voltar para a lista
        </button>
      </div>
    </div>
  );
}
